import styled from "styled-components";
import {useContext, useEffect, useRef, useState} from "react";
import Icon from "../Icon";
import {Form, Spinner} from "react-bootstrap";
import baseTheme from "../../shared/baseTheme";
import {useOnClickOutside} from "../../hooks/useOnClickOutside";
import {scrollbarTrack} from "../../shared/mixins";
import {IFilter} from "../../interfaces/IFilter";
import Button from "../Button";
import {useRouter} from "next/router";
import {useCountryLang} from "../../hooks/useCountryLang";
import {FilterSelectionExpectedListingCountContext} from "../../contexts/FilterSelectionExpectedListingCount";
import {request} from "../../shared/httpInterceptor";
import {ClientContext} from "../../contexts/ClientContext";
import CustomTooltip from "../CustomTooltip";
import {useMessages} from "../../hooks/useMessages";
import {getTheme} from "../../shared/themeUtils";
// @ts-ignore
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";

const FilterContain = styled.div`
    position: relative;

    .close-button {
        position: absolute;
        z-index: 2;
        right: 0;
        height: 100%;
        border-top-right-radius: 24px;
        border-bottom-right-radius: 24px;
        width: ${getTheme("space.4")};
        border: none;
        background: ${getTheme("colors.darkGrey")};
        display: flex;
        justify-content: center;
        align-items: center;

        &:hover {
            background: ${getTheme("colors.darkestGrey")};
        }

        &.opened {
            border: 1px solid ${getTheme("colors.border")};
            border-bottom: 1px solid transparent;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }
`

const FilterItem: any = styled.div`
    position: relative;

    p {
        margin: ${getTheme("space.0")};
    }

    .search-attribute {
        margin: ${getTheme("space.2")} 0;
    }

    .mobile-header {
        font-weight: bold;
        font-size: ${getTheme("fontSizes.large")};
        margin-bottom: ${getTheme("space.2")};
        @media (min-width: ${getTheme("breakpoints.1")}) {
            display: none;
        }
    }

    .range-selector {
        height: 100%;
        display: flex;
        align-items: center;
    }
`;

const FilterHeader = styled.div`
    background-color: ${getTheme("colors.background")};
    border-radius: 24px;
    padding: ${getTheme("space.1")} ${getTheme("space.2")};
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    cursor: pointer;
    min-width: 150px;

    &.internal {
        color: ${getTheme("colors.textOrange")};
    }

    &.opened {
        border: 1px solid ${getTheme("colors.border")};
        border-bottom: 1px solid transparent;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    .selection-info {
        color: ${getTheme("colors.secondaryFont")};
        margin-left: ${getTheme("space.0")};
    }

    p {
        flex: 1;
    }
`;

const FilterBody = styled.div`
    background-color: white;
    padding: ${getTheme("space.2")};
    border: 1px solid ${getTheme("colors.border")};
    border-top: none;
    position: absolute;
    width: 100%;
    z-index: 9;
    -webkit-box-shadow: -1px 8px 8px 1px rgba(0, 0, 0, 0.1);
    box-shadow: -1px 8px 8px 1px rgba(0, 0, 0, 0.1);

    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;

    .upper-actions {
        display: flex;
        font-size: 12px;
        color: ${getTheme("colors.secondaryFont")};

        .reset {
            margin-right: ${getTheme("space.1")};
            cursor: pointer;
        }

        .search-action {
            cursor: pointer;
            display: flex;
            padding-left: ${getTheme("space.1")};
            border-left: 1px solid ${getTheme("colors.border")};

            .search-label {
                margin-left: ${getTheme("space.0")};
            }
        }


        .search-icon {
            cursor: pointer;
        }

        .close-icon {
            position: fixed;
            top: ${getTheme("space.2")};
            right: ${getTheme("space.2")};
            cursor: pointer;
            display: none;
        }
    }

    .scroller {
        max-height: 200px;
        overflow: auto;
        flex: 1;
        margin: ${getTheme("space.1")} ${getTheme("space.0")};
        font-size: ${getTheme("fontSizes.small")};

        ${scrollbarTrack}
    }

    .range-slider {
        margin: ${getTheme("space.2")};
    }

    .current-values {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .metric {
            color: ${getTheme("colors.secondaryFont")};
        }

        input {
            width: 40%;
            font-size: 12px;
            font-weight: bold;
            text-align: center;
        }
    }

    &.inside-drawer {
        position: static;
    }

    &:not(.inside-drawer) {
        @media (max-width: ${getTheme("breakpoints.1")}) {
            position: fixed;
            top: 0;
            left: 0;
            z-index: 990;
            right: 0;
            bottom: 0;
            display: flex;
            flex-direction: column;

            .upper-actions {
                .close-icon {
                    display: block;
                }
            }

            .scroller {
                max-height: calc(100vh - ${getTheme("space.4")});
            }
        }
    }
`;

const FilterOptions = styled.div`
    .value-count {
        margin-left: ${getTheme("space.1")};
        color: ${getTheme("colors.secondaryFont")};
    }
`;

type FilterOptionType = {
  id: string,
  text: string,
  tip?: string,
  tipUrl?: string,
  tipTitle?: string,
  count: number,
  disabled?: boolean,
  internal?: boolean
}

type AttributeFilterProps = {
  name: string,
  tip?: string,
  type: "multiple" | "single" | "range",
  rangeValueLabel?: string,
  showAutocomplete?: boolean,
  options: FilterOptionType[],
  filter: IFilter,
  insideDrawer?: boolean,
  expectedListingCountRequestUrl: string
}


const AttributeFilter = (props: AttributeFilterProps) => {
  const ref = useRef();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(props.options);
  const [searchTerm, setSearchTerm] = useState("");
  const router = useRouter()
  const countryLang = useCountryLang()
  const [hasActiveValues, setHasActiveValues] = useState(false)
  const [selectionInfo, setSelectionInfo] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const messagesQuery = useMessages()
  const {
    filterSelectionExpectedListingCountCtx,
    setFilterSelectionExpectedListingCountCtx
  } = useContext(FilterSelectionExpectedListingCountContext)
  const [expectedListingCount, setExpectedListingCount] = useState(filterSelectionExpectedListingCountCtx)
  const {clientCtx} = useContext(ClientContext)

  // Checkbox selection (Multiple)
  const [selectedOptions, setSelectedOptions] = useState<FilterOptionType[]>([] as FilterOptionType[]);

  // Radio Selection (Single)
  const [selectedSingle, setSelectedSingle] = useState<FilterOptionType>({} as FilterOptionType)

  // Range selection
  const [rangeSelection, setRangeSelection] = useState<[number, number]>([Math.floor(props.filter?.min), Math.floor(props.filter?.max)])

  useOnClickOutside(ref, () => setIsFilterOpen(false));

  useEffect(() => {
    setExpectedListingCount(filterSelectionExpectedListingCountCtx)

    // check if there's a filter query param and extract the values for each attribute
    if (router.query.filter) {
      // getting the opened component attribute
      const routerFilterArray = router.query.filter.toString().split(",")

      const attributeIndex = routerFilterArray.findIndex(entry => {
        return entry.startsWith(props.filter.id)
      })

      const attributeString = routerFilterArray[attributeIndex]

      // extract the values
      let attributeValuesIds;
      let attributeNameString;

      if (attributeString) {
        const attributeValuesString = attributeString.split("=")[1]
        attributeNameString = attributeString.split("=")[0]
        if (attributeValuesString) {
          attributeValuesIds = attributeValuesString.split("|")
        }
      }

      // populate the component
      const selected: FilterOptionType[] = []
      if (attributeValuesIds && !isRange()) {
        attributeValuesIds.map(id => {
          const value = props.options.filter((entry: FilterOptionType) => entry.id == id)
          selected.push(value[0])
        })
        setSelectedOptions(selected)
        setHasActiveValues(true)
        const filterUrl = getFinalFilterUrl(selected)

        if (countryLang) {
          setLoadCount(filterUrl).then(() => {
          })
        }
        setSelectionInfo(`(${selected.length})`)
      }

      // Here we handle range initialization
      if (isRange() && attributeNameString == props.filter.id && attributeValuesIds != undefined) {
        setRangeSelection([parseInt(attributeValuesIds[0]), parseInt(attributeValuesIds[1])])
        setHasActiveValues(true)
      } else {
        setRangeSelection([Math.floor(props.filter.min), Math.floor(props.filter.max)])
      }
    }
  }, [props.filter, router, countryLang, filterSelectionExpectedListingCountCtx])


  const isRadio = () => {
    return props.type === "single";
  }


  const isCheckbox = () => {
    return props.type === "multiple";
  }

  const isRange = () => {
    return props.type === "range";
  }


  const handleMultiSelect = async (option: FilterOptionType) => {
    let options: FilterOptionType[] = [...selectedOptions];
    const alreadySelected = options.some((entry: FilterOptionType) => entry.id == option.id)
    if (!alreadySelected) {
      options.push(option)
      setSelectedOptions(options)
    } else {
      options = options.filter((entry: FilterOptionType) => entry.id != option.id)
      setSelectedOptions(options)
    }
    setSelectionInfo(`(${options.length})`)

    const filterUrl = getFinalFilterUrl(options)
    await setLoadCount(filterUrl)
  }


  const handleSingleSelect = (option: FilterOptionType) => {
    setSelectedSingle(option)
  }


  const updateFilterUrl = async (value: number[]) => {
    const filterUrl = await getRangeFinalFilterUrl(value);
    await setLoadCount(filterUrl);
  };


  const setMin = (min: number) => {
    setRangeSelection([min, rangeSelection[1]]);
  }

  const checkMin = () => {
    if (rangeSelection[0] < props.filter.min) {
      setRangeSelection([props.filter.min, rangeSelection[1]]);
    }

    if (rangeSelection[0] >= rangeSelection[1]) {
      setRangeSelection([rangeSelection[1] - 1, rangeSelection[1]]);
    }
  }

  const setMax = (max: number) => {
    setRangeSelection([rangeSelection[0], max]);
  }

  const checkMax = () => {
    if (rangeSelection[1] > props.filter.max) {
      setRangeSelection([rangeSelection[0], props.filter.max]);
    }

    if (rangeSelection[1] <= rangeSelection[0]) {
      setRangeSelection([rangeSelection[0], rangeSelection[0] + 1]);
    }
  }


  // we check if value is part of the selected options
  // if it is we return true for checked and enable it in the ui
  const isMultiChecked = (opt: FilterOptionType) => {
    return selectedOptions.some((entry: FilterOptionType) => entry && entry.id == opt.id)
  }


  // enable the selected option in the ui
  const isSingleChecked = (opt: FilterOptionType) => {
    if (selectedSingle) {
      return selectedSingle.id == opt.id;
    }
    return false;
  }


  const getFinalFilterUrl = (selected: FilterOptionType[]) => {
    // url filter excluding current attribute
    // (we need it when re-constructing the final filter url containing all the attributes)
    let routerFilterArrayExcludingCurrent: any = [];
    if (router.query.filter) {
      const filterUrlArr = router.query.filter.toString().split(",")
      routerFilterArrayExcludingCurrent = filterUrlArr.filter(item => !item.startsWith(props.filter.id))
    }

    // transform selected options for url persistence
    const selectedTransformedOptions: any = []
    selected.map(item => {
      if (item) {
        selectedTransformedOptions.push(item.id)
      }
    })

    let finalFilterUrl;
    if (selectedTransformedOptions.length > 0) {
      // current attribute filter url
      const currentAttrUrl = `${props.filter.id}=${selectedTransformedOptions.join("|")}`

      // final filter url (all attributes)
      finalFilterUrl = [...routerFilterArrayExcludingCurrent, currentAttrUrl].join(",")
    } else {
      finalFilterUrl = routerFilterArrayExcludingCurrent.join(",")
    }

    return finalFilterUrl;
  }


  const updateUrlExcludingCurrentAttribute = async () => {
    let routerFilterArrayExcludingCurrent: any = [];
    if (router.query.filter) {
      const filterUrlArr = router.query.filter.toString().split(",")
      routerFilterArrayExcludingCurrent = filterUrlArr.filter(item => !item.startsWith(props.filter.id))
    }

    if (routerFilterArrayExcludingCurrent.length > 0) {
      await router.push({
        query: {
          ...(router.query),
          ...(routerFilterArrayExcludingCurrent.length > 0 &&
            {filter: routerFilterArrayExcludingCurrent.join(",")})
        }
      }, undefined, {shallow: true})

      setHasActiveValues(false)
    } else {
      // if there are no other attributes we remove the filter query param
      const {filter, ...excludingFilterUrlQueries} = router.query
      await router.push({
        query: {
          ...(excludingFilterUrlQueries)
        }
      }, undefined, {shallow: true})

      setHasActiveValues(false)
    }
  }


  const handleResetSelection = async () => {
    await updateUrlExcludingCurrentAttribute()
    setSelectedOptions([])
  }


  const handleRangeSelection = async () => {
    setRangeSelection([props.filter.min, props.filter.max])
    await updateFilterUrl([props.filter.min, props.filter.max])
  }


  const handleSubmitSelection = async () => {
    // construct the filter of this component with its selected values
    if (selectedOptions.length > 0) {
      const finalFilterUrl = getFinalFilterUrl(selectedOptions)

      // update url
      await router.push({
        query: {
          ...(router.query),
          ...(finalFilterUrl != null && {filter: finalFilterUrl})
        }
      }, undefined, {shallow: true})

      setHasActiveValues(true)
    } else {
      // if there are no values selected for this attribute
      // we return the other attributes filter url
      await updateUrlExcludingCurrentAttribute()
      // setHasActiveValues(false)
    }

    setIsFilterOpen(false)
  }


  const getRangeFinalFilterUrl = async (range?: number[]) => {
    // url filter excluding current attribute
    // (we need it when re-constructing the final filter url containing all the attributes)
    let routerFilterArrayExcludingCurrent: any = [];
    if (router.query.filter) {
      const filterUrlArr = router.query.filter.toString().split(",")
      routerFilterArrayExcludingCurrent = filterUrlArr.filter(item => !item.startsWith(props.filter.id))
    }

    let finalFilterUrl;
    // current attribute filter url
    const currentAttrUrl = `${props.filter.id}=${range ? range.join("|") : rangeSelection.join("|")}`

    // final filter url (all attributes)
    finalFilterUrl = [...routerFilterArrayExcludingCurrent, currentAttrUrl].join(",")

    return finalFilterUrl;
  }


  const handleSubmitRange = async () => {
    // construct the filter of this component with its selected values
    const finalFilterUrl = await getRangeFinalFilterUrl()

    if (finalFilterUrl) {
      // update url
      await router.push({
        query: {
          ...(router.query),
          ...(({filter: finalFilterUrl}))
        }
      }, undefined, {shallow: true})

      setHasActiveValues(true)
    }

    setIsFilterOpen(false)
  }


  const setLoadCount = async (filterUrl: string) => {
    // populate expected listing count
    setIsLoading(true)
    const res = await request({
      url: `/${countryLang}/${props.expectedListingCountRequestUrl}?filter=${encodeURIComponent(filterUrl)}`,
      method: 'get'
    });
    setExpectedListingCount(res.data.filterProductCount)
    setIsLoading(false)
  }


  const getLabelContent = (option: FilterOptionType) => {
    return (
      <div>
        {/*{!option.tip && !(option.tip && option.tip.length>0) && option.text}*/}
        {option.text}
        {option.tip != null && (option.tip && option.tip.length > 0) &&
          <CustomTooltip placement={"right"}
                         content={option.tip}
                         linkUrl={option.tipUrl}
                         title={option.tipTitle}
          />
        }
        {option.count > 0 && <span className={"value-count"}>({option.count})</span>}
        {option.disabled && <span className={"value-count"}>({"0"})</span>}
      </div>
    )
  }


  const renderCheckboxes = () => {
    return <FilterOptions>
      {
        filteredOptions?.map((opt: FilterOptionType) => (
          <Form.Check
            key={opt.id}
            style={{color: opt.internal ? baseTheme.colors.textOrange : ""}}
            checked={isMultiChecked(opt)}
            onChange={() => handleMultiSelect(opt)}
            type="checkbox"
            label={getLabelContent(opt)}
            disabled={opt.disabled}
            id={opt.id}>
          </Form.Check>
        ))
      }
    </FilterOptions>
  }


  const renderRadioButtons = () => {
    return <FilterOptions>
      {
        filteredOptions?.map((opt: FilterOptionType) => (
          <Form.Check
            key={opt.id}
            checked={isSingleChecked(opt)}
            type="radio"
            onChange={() => handleSingleSelect(opt)}
            label={getLabelContent(opt)}
            id={opt.id}>
          </Form.Check>
        ))
      }
    </FilterOptions>
  }


  const renderRangeSelection = () => {
    return (
      <div className={"w-100"}>
        <RangeSlider value={rangeSelection}
                     onInput={setRangeSelection}
                     min={Math.floor(props.filter.min)}
                     max={Math.floor(props.filter.max)}
                     onThumbDragEnd={() => updateFilterUrl(rangeSelection)}
        />

        <div className="current-values">
          <Form.Control type="number" value={rangeSelection[0]}
                        min={Math.floor(props.filter.min)}
                        max={Math.floor(props.filter.max)}
                        onBlur={(e) => checkMin()}
                        onChange={(e) => setMin(+e.target.value)}/>
          <span className="metric">{props.rangeValueLabel}</span>
          <Form.Control type="number"
                        min={Math.floor(props.filter.min)}
                        max={Math.floor(props.filter.max)}
                        value={rangeSelection[1]}
                        onBlur={(e) => checkMax()}
                        onChange={(e) => setMax(+e.target.value)}/>
        </div>
      </div>
    )
  }


  const filterAttributes = (event: any) => {
    setSearchTerm(event.target.value);

    if (event.target.value && event.target.value !== "") {
      const filteredValues = props.options?.filter(v => v.text.toLowerCase().indexOf(event.target.value.toLowerCase()) > -1);
      setFilteredOptions(filteredValues);
    } else {
      setFilteredOptions(props.options);
    }
  }

  const getHeaderClasses = (filter: IFilter, classes?: string) => {
    let cls = classes;
    if (filter.internal) {
      cls += " internal"
    }

    return cls
  }


  return (
    <FilterContain>
      {hasActiveValues && <button className={isFilterOpen ? "close-button opened" : "close-button"}
                                  onClick={handleResetSelection}><Icon variant={"medium"}>close-line</Icon></button>}
      <FilterItem ref={ref}>
        <FilterHeader onClick={() => setIsFilterOpen(!isFilterOpen)}
                      className={getHeaderClasses(props.filter, isFilterOpen ? "opened" : "")}>
          <p>
            {props.name}
            {props.tip != null && (props.tip && props.tip.length > 0) &&
              <CustomTooltip placement={"right"}
                             content={props.tip}
              />
            }
            {hasActiveValues && <span className="selection-info">{selectionInfo}</span>}
          </p>
          <Icon variant={"medium"}>arrow-down-s-line</Icon>
        </FilterHeader>
        {
          isFilterOpen && <FilterBody className={props.insideDrawer ? "inside-drawer" : ""}>
            <div className="mobile-header">
              {props.name}
            </div>

            <div className="upper-actions">
              {messagesQuery.data &&
                <>
                  {!isRange() && <span onClick={handleResetSelection}
                                       className="reset">{messagesQuery.data.messages["reset"]}</span>}
                  {isRange() && <span onClick={handleRangeSelection}
                                      className="reset">{messagesQuery.data.messages["reset"]}</span>}
                </>
              }

              <Icon className="close-icon" variant="small" color={baseTheme.colors.primary}
                    onClick={() => setIsFilterOpen(false)}>close-line</Icon>

              {!isRange() && messagesQuery.data &&
                <div className="search-action" onClick={() => setIsSearchVisible(!isSearchVisible)}>
                  <Icon color={baseTheme.colors.secondaryFont} variant="xSmall" className="search-icon">search-line</Icon>
                  <span className="search-label">{messagesQuery.data.messages["search-button"]}</span>

                </div>}
            </div>
            {
              isSearchVisible && messagesQuery.data && <div className="search-attribute">
                <Form.Control value={searchTerm} onChange={(e) => filterAttributes(e)}
                              placeholder={messagesQuery.data.messages["search-button"] + "..."}/>
              </div>
            }
            <div className="scroller">
              {isRadio() && renderRadioButtons()}
              {isCheckbox() && renderCheckboxes()}
            </div>
            {isRange() && <div className={"range-selector"}>{renderRangeSelection()}</div>}
            <div className="action-buttons">
              {!isRange() &&
                <>
                  {((hasActiveValues || selectedOptions.length > 0) && !isLoading) && messagesQuery.data &&
                    <Button variant={"primary"} width="100%" onClick={handleSubmitSelection}>
                      {messagesQuery.data.messages["expected-listing-count"].replace("%s", expectedListingCount)}
                    </Button>}

                  {((!hasActiveValues && selectedOptions.length == 0) && !isLoading) && messagesQuery.data &&
                    <Button variant="secondary"
                            width="100%"
                            onClick={() => setIsFilterOpen(false)}>{messagesQuery.data.messages["close"]}</Button>}
                </>}

              {isRange() && !isLoading && messagesQuery.data &&
                <Button variant={"primary"}
                        className={"mt-3"}
                        width="100%"
                        onClick={handleSubmitRange}>
                  {messagesQuery.data.messages["expected-listing-count"].replace("%s", expectedListingCount)}
                </Button>
              }

              {isRange() && isLoading &&
                <Button variant={"primary"}
                        className={"mt-3"}
                        disabled
                        width="100%">
                  <Spinner animation="border" size="sm"/>
                </Button>
              }

              {!isRange() && isLoading &&
                <Button variant={"primary"}
                        disabled
                        width="100%">
                  <Spinner animation="border" size="sm"/>
                </Button>
              }

            </div>
          </FilterBody>
        }
      </FilterItem>
    </FilterContain>
  )
}

export default AttributeFilter;
